import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Calendar, AlertTriangle, Loader2, Trash2, List, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { adminApi } from "../../lib/api";
import { DUPLICATE_REASONS } from "../../lib/jobConstants";
import BookingsWeekView from "../../components/admin/BookingsWeekView";

const inputCls =
  "bg-white border-[#cfd7db] text-[#111619] placeholder:text-[#9aa6ad] focus:border-[#f28a16] focus:ring-1 focus:ring-[#f28a16] rounded-md h-10";

const newBlank = {
  client_name: "", registration: "", vehicle_make_model: "",
  fitment_date: "", fitment_time: "", fitter_team_leader: "",
  location: "", notes: "", duplicate_reason: "",
};

export const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(newBlank);
  const [editingId, setEditingId] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("list"); // 'list' | 'week'

  const load = async () => {
    setLoading(true);
    try {
      const [b, j] = await Promise.all([
        adminApi.listBookings(),
        adminApi.listJobs(),
      ]);
      setBookings(b);
      setJobs(j);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(newBlank);
    setEditingId(null);
    setDuplicates([]);
    setShowForm(true);
  };

  const openEdit = (b) => {
    // Coalesce nulls from API into empty strings so controlled inputs don't warn
    const clean = Object.fromEntries(
      Object.entries(b).map(([k, v]) => [k, v ?? ""])
    );
    setForm({ ...newBlank, ...clean });
    setEditingId(b.id);
    setDuplicates([]);
    setShowForm(true);
  };

  const openCreateAt = (dateISO, timeStr) => {
    setForm({ ...newBlank, fitment_date: dateISO, fitment_time: timeStr });
    setEditingId(null);
    setDuplicates([]);
    setShowForm(true);
  };

  const onSelectJob = (jobId) => {
    if (!jobId) return;
    const j = jobs.find((x) => x.id === jobId);
    if (!j) return;
    setForm((f) => ({
      ...f,
      client_name: j.client_name || f.client_name,
      registration: j.registration || f.registration,
      vehicle_make_model: j.vehicle_make_model || f.vehicle_make_model,
      fitter_team_leader: j.fitter_team_leader || f.fitter_team_leader,
      location: j.fitment_address || f.location,
      fitment_date: j.fitment_date || f.fitment_date,
      fitment_time: j.fitment_time || f.fitment_time,
      job_id: j.id,
    }));
  };

  const checkDuplicate = async () => {
    if (!form.registration) return;
    setChecking(true);
    try {
      const r = await adminApi.checkBookingDuplicate(form.registration);
      const filtered = (r.duplicates || []).filter((d) => d.id !== editingId);
      setDuplicates(filtered);
    } catch (e) {
      // ignore
    } finally {
      setChecking(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.registration.trim() || !form.fitment_date) {
      toast.error("Client name, registration and fitment date are required.");
      return;
    }
    // Ensure duplicate check has run
    if (duplicates.length > 0 && !form.duplicate_reason) {
      toast.error("A booking already exists for this registration. Choose a duplicate reason.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await adminApi.updateBooking(editingId, form);
        toast.success("Booking updated");
      } else {
        await adminApi.createBooking(form);
        toast.success("Booking created");
      }
      setShowForm(false);
      setForm(newBlank);
      setEditingId(null);
      setDuplicates([]);
      load();
    } catch (err) {
      console.error(err);
      toast.error("Could not save booking");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Cancel / delete this booking?")) return;
    await adminApi.deleteBooking(id);
    toast.success("Booking removed");
    load();
  };

  // Group bookings by date for a simple agenda view
  const grouped = bookings.reduce((acc, b) => {
    (acc[b.fitment_date] = acc[b.fitment_date] || []).push(b);
    return acc;
  }, {});
  const dateKeys = Object.keys(grouped).sort();

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Bookings</h1>
          <p className="text-[#3f4b52] mt-1">Schedule fitments. Duplicate registrations need a reason.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div className="inline-flex bg-white border border-[#cfd7db] rounded-md overflow-hidden shadow-sm" data-testid="bookings-view-toggle">
            <button
              onClick={() => setView("list")}
              data-testid="view-list-btn"
              className={`px-3 h-11 inline-flex items-center gap-1.5 text-xs font-display uppercase tracking-widest transition ${
                view === "list"
                  ? "bg-[#f28a16] text-white"
                  : "text-[#3f4b52] hover:text-[#f28a16]"
              }`}
            >
              <List size={14} /> List
            </button>
            <button
              onClick={() => setView("week")}
              data-testid="view-week-btn"
              className={`px-3 h-11 inline-flex items-center gap-1.5 text-xs font-display uppercase tracking-widest transition border-l border-[#cfd7db] ${
                view === "week"
                  ? "bg-[#f28a16] text-white"
                  : "text-[#3f4b52] hover:text-[#f28a16]"
              }`}
            >
              <LayoutGrid size={14} /> Week
            </button>
          </div>
          <button
            onClick={openCreate}
            data-testid="bookings-new-btn"
            className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-sm px-5 h-11 rounded-md inline-flex items-center gap-2 hover:brightness-110 transition shadow"
          >
            <Plus size={16} /> New booking
          </button>
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={onSubmit}
          data-testid="booking-form"
          className="mt-6 bg-white border border-[#cfd7db] rounded-md p-6 shadow-sm"
        >
          <h2 className="font-display font-bold uppercase tracking-wider text-lg text-[#111619] mb-5 border-l-4 border-[#f28a16] pl-3">
            {editingId ? "Edit booking" : "New booking"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
            <div className="lg:col-span-3">
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">
                Pre-fill from existing job (optional)
              </Label>
              <select
                onChange={(e) => onSelectJob(e.target.value)}
                className={`${inputCls} px-3 mt-1.5 w-full`}
                data-testid="booking-job-select"
                defaultValue=""
              >
                <option value="">— Select a job —</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {`#${j.job_number} · ${j.client_name} · ${j.registration || "no reg"} · ${j.vehicle_make_model || ""}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Client name *</Label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="booking-client" />
            </div>
            <div>
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Registration *</Label>
              <Input
                value={form.registration}
                onChange={(e) => setForm({ ...form, registration: e.target.value })}
                onBlur={checkDuplicate}
                className={`${inputCls} mt-1.5`}
                data-testid="booking-reg"
              />
            </div>
            <div>
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Vehicle</Label>
              <Input value={form.vehicle_make_model} onChange={(e) => setForm({ ...form, vehicle_make_model: e.target.value })} className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Fitment date *</Label>
              <Input type="date" value={form.fitment_date} onChange={(e) => setForm({ ...form, fitment_date: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="booking-date" />
            </div>
            <div>
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Time</Label>
              <Input value={form.fitment_time} onChange={(e) => setForm({ ...form, fitment_time: e.target.value })} placeholder="08H00" className={`${inputCls} mt-1.5`} data-testid="booking-time" />
            </div>
            <div>
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Fitter / team leader</Label>
              <Input value={form.fitter_team_leader} onChange={(e) => setForm({ ...form, fitter_team_leader: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="booking-fitter" />
            </div>
            <div className="lg:col-span-3">
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Location / address</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={`${inputCls} mt-1.5`} />
            </div>
            <div className="lg:col-span-3">
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={`${inputCls} h-auto py-2 mt-1.5`} />
            </div>
          </div>

          {/* Duplicate warning */}
          {checking && (
            <div className="mt-4 text-xs text-[#3f4b52] inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" /> Checking for duplicates…
            </div>
          )}
          {duplicates.length > 0 && (
            <div className="mt-5 bg-[#fff7ed] border border-[#fb923c] rounded-md p-4" data-testid="booking-duplicate-warning">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={18} className="text-[#dc2626] shrink-0 mt-0.5" />
                <div>
                  <div className="font-display uppercase tracking-wider text-sm text-[#7c2d12]">
                    Duplicate registration found
                  </div>
                  <p className="text-xs text-[#7c2d12]/90 mt-1">
                    This vehicle is already booked. To proceed, select a reason for the duplicate booking.
                  </p>
                </div>
              </div>
              <ul className="text-xs text-[#7c2d12] list-disc pl-6 mb-3">
                {duplicates.map((d) => (
                  <li key={d.id}>
                    {d.fitment_date} {d.fitment_time || ""} · {d.client_name} · {d.fitter_team_leader || "no fitter"} · status: {d.status}
                  </li>
                ))}
              </ul>
              <Label className="font-display uppercase tracking-widest text-xs text-[#7c2d12]">
                Reason for duplicate booking *
              </Label>
              <select
                value={form.duplicate_reason}
                onChange={(e) => setForm({ ...form, duplicate_reason: e.target.value })}
                className={`${inputCls} px-3 mt-1.5 w-full`}
                data-testid="booking-duplicate-reason"
              >
                <option value="">— Select a reason —</option>
                {DUPLICATE_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); setEditingId(null); }}
              className="px-5 h-10 border border-[#cfd7db] rounded-md text-sm font-display uppercase tracking-wider text-[#3f4b52] hover:bg-[#edf2f4]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="booking-save"
              className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-sm px-6 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 shadow"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {editingId ? "Save changes" : "Create booking"}
            </button>
          </div>
        </form>
      )}

      {/* Week view */}
      {!loading && view === "week" && (
        <BookingsWeekView
          bookings={bookings}
          onEdit={openEdit}
          onCreateAt={openCreateAt}
        />
      )}

      {/* List grouped by date */}
      {view === "list" && (
      <div className="mt-8 space-y-6">
        {loading && <div className="text-[#3f4b52]">Loading…</div>}
        {!loading && bookings.length === 0 && (
          <div className="bg-white border border-[#cfd7db] rounded-md p-10 text-center text-[#3f4b52]">
            No bookings yet. Click "New booking" to schedule a fitment.
          </div>
        )}
        {dateKeys.map((d) => (
          <div key={d} className="bg-white border border-[#cfd7db] rounded-md shadow-sm">
            <div className="px-5 py-3 border-b border-[#eef2f4] flex items-center gap-3 bg-[#edf2f4]">
              <Calendar size={16} className="text-[#f28a16]" />
              <div className="font-display uppercase tracking-widest text-sm text-[#111619]">{d}</div>
              <span className="text-xs text-[#3f4b52]">{grouped[d].length} booking(s)</span>
            </div>
            <ul className="divide-y divide-[#eef2f4]">
              {grouped[d].map((b) => (
                <li key={b.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`booking-row-${b.id}`}>
                  <div className="text-sm">
                    <span className="font-display font-bold text-[#f28a16]">{b.fitment_time || "—"}</span>{" "}
                    <span className="text-[#111619]">· {b.registration}</span>{" "}
                    <span className="text-[#3f4b52]">· {b.vehicle_make_model || ""}</span>{" "}
                    <span className="text-[#3f4b52]">· {b.client_name}</span>{" "}
                    {b.fitter_team_leader && <span className="text-[#3f4b52]">· {b.fitter_team_leader}</span>}
                    {b.duplicate_reason && (
                      <div className="text-[11px] text-[#dc2626] mt-1">⚠ Duplicate: {b.duplicate_reason}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(b)}
                      className="text-xs font-display uppercase tracking-wider text-[#3f4b52] hover:text-[#f28a16]"
                      data-testid={`booking-edit-${b.id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(b.id)}
                      className="text-xs text-[#dc2626] hover:text-red-700 inline-flex items-center gap-1"
                      data-testid={`booking-delete-${b.id}`}
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default AdminBookings;
